import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, Radio, Eye, LogIn, Database, Cpu, ScrollText, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className={`h-4 w-4 ${accent || "text-gold-400"}`} /><span className="text-xs uppercase tracking-wider">{label}</span></div>
    <p className={`mt-2 font-display text-3xl font-bold ${accent || "text-gold-400"}`}>{value}</p>
  </div>
);

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const load = () => api.get("/admin/analytics").then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user]);

  if (user && user.role !== "admin")
    return <div className="grid h-64 place-items-center text-muted-foreground" data-testid="admin-denied"><div className="text-center"><ShieldCheck className="mx-auto mb-2 h-8 w-8" />Accès réservé à l'administrateur.</div></div>;
  if (!d) return <div className="grid h-64 place-items-center text-muted-foreground">Chargement…</div>;

  const maxModel = Math.max(1, ...d.top_models.map((m) => m.requests));

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-400">Admin sécurisé</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">Analytics du site</h1>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2" data-testid="online-now">
          <Radio className="h-4 w-4 text-emerald-400" />
          <span className="text-sm"><span className="font-display text-lg font-bold text-emerald-400">{d.online_visitors}</span> <span className="text-muted-foreground">en ligne</span> · <span className="font-mono text-emerald-400">{d.online_users}</span> connecté(s)</span>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat icon={Users} label="Comptes" value={d.users_total} testid="stat-users" />
        <Stat icon={Eye} label="Visiteurs" value={d.visitors_total} testid="stat-visitors" />
        <Stat icon={Eye} label="Visites aujourd'hui" value={d.visits_today} testid="stat-visits-today" />
        <Stat icon={LogIn} label="Connexions" value={d.logins_total} testid="stat-logins" />
        <Stat icon={Eye} label="Pages vues" value={d.pageviews_total} testid="stat-pageviews" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-2" data-testid="visits-chart">
          <h3 className="mb-4 font-display font-semibold">Trafic — 7 derniers jours</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={d.visits_series}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0F24", border: "1px solid #1E294B", borderRadius: 12, color: "#F8FAFC" }} cursor={{ stroke: "#D4AF37" }} />
              <Area type="monotone" dataKey="visits" stroke="#D4AF37" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5" data-testid="top-models">
          <div className="mb-3 flex items-center gap-2"><Cpu className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Produit le plus sollicité</h3></div>
          <div className="space-y-3">
            {d.top_models.length === 0 && <p className="text-sm text-muted-foreground">Aucun usage.</p>}
            {d.top_models.map((m) => (
              <div key={m.model} data-testid={`model-row-${m.model}`}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-mono text-xs">{m.model}</span>
                  <span className="text-muted-foreground">{m.requests} · <span className="text-gold-400">{m.credits} cr</span></span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-gold-500" style={{ width: `${(m.requests / maxModel) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* DB overview */}
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="db-overview">
          <div className="mb-3 flex items-center gap-2"><Database className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Base de données</h3></div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {d.db_overview.map((c) => (
              <div key={c.collection} className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={`db-${c.collection}`}>
                <p className="font-mono text-xs text-muted-foreground">{c.collection}</p>
                <p className="font-display text-xl font-bold text-gold-400">{c.count.toLocaleString("fr-FR")}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top pages */}
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="top-pages">
          <div className="mb-3 flex items-center gap-2"><Eye className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Pages les plus vues</h3></div>
          <div className="space-y-2">
            {d.top_pages.length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
            {d.top_pages.map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm">
                <span className="font-mono text-xs">{p.path}</span>
                <span className="text-gold-400">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Recent users */}
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="recent-users">
          <div className="mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Derniers comptes</h3></div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {d.recent_users.map((u) => (
              <div key={u.id} className="flex items-center justify-between border-b border-border/60 py-1.5 text-sm last:border-0">
                <div className="min-w-0"><p className="truncate">{u.name || u.email}</p><p className="truncate font-mono text-xs text-muted-foreground">{u.email}</p></div>
                <div className="text-right"><span className="rounded bg-secondary px-1.5 py-0.5 text-xs">{u.role}</span><p className="mt-0.5 font-mono text-xs text-gold-400">{(u.credits ?? 0).toLocaleString("fr-FR")} cr</p></div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent events */}
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="recent-events">
          <div className="mb-3 flex items-center gap-2"><ScrollText className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Flux d'activité récent</h3></div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {d.recent_events.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className={`h-1.5 w-1.5 rounded-full ${e.kind === "login" ? "bg-emerald-400" : e.kind === "signup" ? "bg-gold-400" : "bg-slate-500"}`} />
                  <span className="text-muted-foreground">{e.kind}</span>
                  <span className="font-mono text-xs">{e.email || e.path || ""}</span>
                </span>
                <span className="font-mono text-[11px] text-muted-foreground">{new Date(e.ts).toLocaleTimeString("fr-FR")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
